import { useCallback, useEffect, useRef } from 'react';

type InfiniteScrollProps = {
    fetchNextPage: () => void;
    isFetching?: boolean;
    root?: Element | null;
};

export const useInfiniteScroll = ({
    fetchNextPage,
    isFetching,
    root = null,
}: InfiniteScrollProps) => {
    const observerTarget = useRef<HTMLElement>();
    const observerRef = useRef<IntersectionObserver>();
    const isFetchingRef = useRef<boolean>(false);

    // Memoize the intersection callback to prevent unnecessary observer recreations
    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (
                    entry.isIntersecting &&
                    !isFetchingRef.current &&
                    !isFetching
                ) {
                    isFetchingRef.current = true;
                    fetchNextPage();
                    // Reset the flag after a short delay to prevent rapid successive calls
                    setTimeout(() => {
                        isFetchingRef.current = false;
                    }, 100);
                }
            });
        },
        [fetchNextPage, isFetching],
    );

    useEffect(() => {
        // Clean up previous observer if it exists
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer
        observerRef.current = new IntersectionObserver(handleIntersection, {
            threshold: 0.1,
            root: root,
            rootMargin: '20px', // Trigger slightly before the element comes into view
        });

        // Observe the target element
        if (observerTarget.current && observerRef.current) {
            observerRef.current.observe(observerTarget.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = undefined;
            }
        };
    }, [handleIntersection, root]);

    // Update the fetching state when isFetching prop changes
    useEffect(() => {
        if (!isFetching) {
            isFetchingRef.current = false;
        }
    }, [isFetching]);

    return observerTarget;
};
